import { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ArrowLeft, Download } from 'lucide-react';
import { mockPreAssessedFees, mockFees, mockStudents, mockSchoolYears } from '../../mock';
import { Course, Fee, PreAssessedFee, Student, PricingTier } from '../../types';

interface CourseStudentListProps {
  course: Course;
  onBack: () => void;
}

interface StudentFeeData {
  student: Student;
  fees: {
    fee: Fee;
    preAssessedFee: PreAssessedFee;
  }[];
  totalAssessed: number;
  totalPaid: number;
  balance: number;
  pastDue: number;
}

export function CourseStudentList({ course, onBack }: CourseStudentListProps) {
  const [selectedSchoolYearId, setSelectedSchoolYearId] = useState<string>('all');

  const getPriceForTier = (fee: Fee, tier: PricingTier): number => {
    switch (tier) {
      case 'full': return fee.basePrice;
      case 'reduced': return fee.reducedPrice;
      case 'free': return fee.freePrice;
      default: return fee.basePrice;
    }
  };

  const courseFees = useMemo(() => {
    return mockFees.filter(fee => {
      if (!fee.course) return false;

      const courseLower = fee.course.toLowerCase();
      const nameLower = course.name.toLowerCase();
      const codeLower = course.code.toLowerCase();

      return courseLower.includes(nameLower) ||
             courseLower.includes(codeLower) ||
             nameLower.includes(courseLower);
    });
  }, [course]);

  const studentData = useMemo(() => {
    const dataMap = new Map<string, StudentFeeData>();

    courseFees.forEach(fee => {
      const preAssessedForFee = mockPreAssessedFees.filter(paf => paf.feeId === fee.id);

      preAssessedForFee.forEach(paf => {
        if (selectedSchoolYearId !== 'all') {
          const feeMatchesYear = fee.schoolYearIds.includes(selectedSchoolYearId);
          if (!feeMatchesYear) return;
        }

        const student = mockStudents.find(s => s.id === paf.studentId);
        if (!student) return;

        const unitPrice = getPriceForTier(fee, paf.pricingTier);
        const total = unitPrice * paf.quantity;
        const paid = paf.amountPaid || 0;
        const balance = total - paid;
        const isPastDue = paf.status === 'overdue' ? balance : 0;

        if (!dataMap.has(student.id)) {
          dataMap.set(student.id, {
            student,
            fees: [],
            totalAssessed: 0,
            totalPaid: 0,
            balance: 0,
            pastDue: 0,
          });
        }

        const data = dataMap.get(student.id)!;
        data.fees.push({ fee, preAssessedFee: paf });
        data.totalAssessed += total;
        data.totalPaid += paid;
        data.balance += balance;
        data.pastDue += isPastDue;
      });
    });

    return Array.from(dataMap.values()).sort((a, b) =>
      a.student.lastName.localeCompare(b.student.lastName)
    );
  }, [courseFees, selectedSchoolYearId]);

  const summary = useMemo(() => {
    return studentData.reduce((acc, data) => {
      acc.totalStudents++;
      acc.totalAssessed += data.totalAssessed;
      acc.totalPaid += data.totalPaid;
      acc.totalBalance += data.balance;
      acc.totalPastDue += data.pastDue;

      if (data.balance === 0) acc.studentsFullyPaid++;
      if (data.pastDue > 0) acc.studentsOverdue++;

      return acc;
    }, {
      totalStudents: 0,
      totalAssessed: 0,
      totalPaid: 0,
      totalBalance: 0,
      totalPastDue: 0,
      studentsFullyPaid: 0,
      studentsOverdue: 0,
    });
  }, [studentData]);

  const handleExportCSV = () => {
    const headers = [
      'Student ID',
      'Last Name',
      'First Name',
      'Grade',
      'Email',
      'Pricing Tier',
      'Total Assessed',
      'Total Paid',
      'Balance',
      'Past Due',
      'Fees Count'
    ];

    const rows = studentData.map(data => [
      data.student.studentIdNumber,
      data.student.lastName,
      data.student.firstName,
      data.student.grade,
      data.student.email,
      data.student.pricingTier,
      data.totalAssessed.toFixed(2),
      data.totalPaid.toFixed(2),
      data.balance.toFixed(2),
      data.pastDue.toFixed(2),
      data.fees.length,
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `${course.code}_students_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowLeft size={18} />}
          onClick={onBack}
        >
          Back to Courses
        </Button>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={700}>
            {course.name} ({course.code})
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Students assessed for this course
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Download size={18} />}
          onClick={handleExportCSV}
          disabled={studentData.length === 0}
          sx={{
            bgcolor: '#1e293b',
            '&:hover': {
              bgcolor: '#0f172a',
            },
          }}
        >
          Export CSV
        </Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight={600}>
              Course Summary
            </Typography>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>School Year</InputLabel>
              <Select
                value={selectedSchoolYearId}
                onChange={(e) => setSelectedSchoolYearId(e.target.value)}
                label="School Year"
              >
                <MenuItem value="all">All School Years</MenuItem>
                {mockSchoolYears.filter(y => y.isActive).map(year => (
                  <MenuItem key={year.id} value={year.id}>
                    {year.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 3 }}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total Students
              </Typography>
              <Typography variant="h5" fontWeight={700}>
                {summary.totalStudents}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {summary.studentsFullyPaid} fully paid
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total Assessed
              </Typography>
              <Typography variant="h5" fontWeight={700}>
                ${summary.totalAssessed.toFixed(2)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {courseFees.length} fee{courseFees.length !== 1 ? 's' : ''} for course
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total Collected
              </Typography>
              <Typography variant="h5" fontWeight={700} color="success.main">
                ${summary.totalPaid.toFixed(2)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {summary.totalAssessed > 0 ? ((summary.totalPaid / summary.totalAssessed) * 100).toFixed(1) : 0}% collected
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Past Due
              </Typography>
              <Typography variant="h5" fontWeight={700} color={summary.totalPastDue > 0 ? 'error.main' : 'text.primary'}>
                ${summary.totalPastDue.toFixed(2)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {summary.studentsOverdue} student{summary.studentsOverdue !== 1 ? 's' : ''} overdue
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {studentData.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Students Assessed
          </Typography>
          <Typography variant="body2" color="text.secondary">
            No students have been assessed fees for this course
            {selectedSchoolYearId !== 'all' ? ' in the selected school year' : ''}.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Student ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Grade</TableCell>
                <TableCell>Pricing Tier</TableCell>
                <TableCell align="right">Total Assessed</TableCell>
                <TableCell align="right">Total Paid</TableCell>
                <TableCell align="right">Balance</TableCell>
                <TableCell align="right">Past Due</TableCell>
                <TableCell align="center">Fees</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {studentData.map((data) => (
                <TableRow key={data.student.id} hover>
                  <TableCell>{data.student.studentIdNumber}</TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {data.student.lastName}, {data.student.firstName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {data.student.email}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{data.student.grade}</TableCell>
                  <TableCell>
                    <Chip
                      label={data.student.pricingTier}
                      size="small"
                      color={
                        data.student.pricingTier === 'free' ? 'success' :
                        data.student.pricingTier === 'reduced' ? 'warning' :
                        'default'
                      }
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    ${data.totalAssessed.toFixed(2)}
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'success.main', fontWeight: 600 }}>
                    ${data.totalPaid.toFixed(2)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: data.balance > 0 ? 'warning.main' : 'inherit' }}>
                    ${data.balance.toFixed(2)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: data.pastDue > 0 ? 'error.main' : 'inherit' }}>
                    ${data.pastDue.toFixed(2)}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={data.fees.length}
                      size="small"
                      sx={{ minWidth: 40 }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
